import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, ArrowLeft, Sparkles, ShieldCheck, Check } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLoginButton } from '../components/GoogleLoginButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SignupPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', admin_name: '', admin_email: '', admin_password: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.admin_name || !form.admin_email || !form.admin_password) {
      toast.error('Preencha todos os campos.');
      return;
    }
    if (form.admin_password.length < 8) {
      toast.error('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (!acceptedTerms) {
      toast.error('É necessário aceitar a Política de Privacidade e os Termos de Uso.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/signup`, form, { withCredentials: true });
      // Login automaticamente
      await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email: form.admin_email, password: form.admin_password },
        { withCredentials: true }
      );
      toast.success('Cadastro realizado! Bem-vindo ao MeAdd.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid grid-cols-1 lg:grid-cols-2" data-testid="signup-page">
      {/* Form side */}
      <div className="flex items-center justify-center p-8 md:p-12 bg-white">
        <div className="w-full max-w-md space-y-8">
          <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 font-manrope">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Voltar para o site
          </Link>

          <div>
            <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-10 mb-6" />
            <h1 className="font-outfit font-bold text-3xl text-slate-900 mb-2">
              Comece grátis por 7 dias
            </h1>
            <p className="text-slate-600 font-manrope">
              Acesso completo a todas as funcionalidades. Sem cartão de crédito.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Nome da campanha
              </Label>
              <Input
                id="name"
                placeholder="Ex: Candidato João da Silva"
                value={form.name}
                onChange={update('name')}
                data-testid="signup-name"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="admin_name" className="text-sm font-medium text-slate-700">
                Seu nome
              </Label>
              <Input
                id="admin_name"
                placeholder="Como devemos te chamar?"
                value={form.admin_name}
                onChange={update('admin_name')}
                data-testid="signup-admin-name"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="voce@email.com"
                value={form.admin_email}
                onChange={update('admin_email')}
                data-testid="signup-email"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Senha (mínimo 8 caracteres)
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={form.admin_password}
                onChange={update('admin_password')}
                data-testid="signup-password"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              size="lg"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-manrope rounded-full h-12 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="signup-submit-btn"
            >
              {loading ? 'Criando conta...' : (
                <>
                  Criar conta grátis <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1" data-testid="signup-terms-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                data-testid="signup-terms-checkbox"
              />
              <span className="text-xs text-slate-600 font-manrope leading-relaxed">
                Li e aceito a{' '}
                <Link to="/privacidade" target="_blank" className="text-emerald-700 font-semibold hover:underline" data-testid="signup-privacy-link">
                  Política de Privacidade
                </Link>{' '}
                e os{' '}
                <Link to="/termos" target="_blank" className="text-emerald-700 font-semibold hover:underline" data-testid="signup-terms-link">
                  Termos de Uso
                </Link>{' '}
                da plataforma MeAdd, operada pela Smart7 Digital Ltda.
              </span>
            </label>
          </form>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-manrope">ou</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>
          <GoogleLoginButton label="Cadastrar com Google" testid="signup-google-btn" />

          <p className="text-center text-sm text-slate-500 font-manrope">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-slate-900 font-semibold hover:underline">
              Entre aqui
            </Link>
          </p>
        </div>
      </div>

      {/* Visual side */}
      <div className="hidden lg:block relative bg-slate-900 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative h-full flex flex-col justify-center p-16 text-white">
          <div className="space-y-8 max-w-md">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 px-3 py-1 rounded-full text-xs uppercase tracking-wider text-emerald-300 font-manrope font-semibold">
              <Sparkles className="h-3 w-3" /> 7 dias grátis
            </div>
            <h2 className="font-outfit font-bold text-4xl leading-tight">
              Tudo o que sua campanha de 2026 precisa, em uma única plataforma.
            </h2>
            <div className="space-y-4 pt-4">
              {[
                'Mobilização: links curtos, NFC, QR Code, heatmap, network',
                'Financeiro: orçamento, gastos com CPF/CNPJ, prestação em Excel',
                'Compliance eleitoral total — pré-campanha e campanha',
                'Suporte humano por e-mail durante todo o período eleitoral',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-emerald-500/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-emerald-300" />
                  </div>
                  <span className="font-manrope text-slate-200 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
            <div className="pt-6 flex items-center gap-2 text-sm text-slate-400 font-manrope">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Plataforma operada pela Smart7 Digital Ltda.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
